import React, { useState, useEffect } from 'react';
import { X, ChevronRight, ChevronDown, ChevronLeft, User, Layers } from 'lucide-react';
import {
  AdminClientApi,
  ClientBelief,
  GetBeliefsInputInterface,
  GetSupportingBeliefCountFromBeliefsInputInterface,
} from '../Admin-ClientLibrary';
import { getApiUrl } from '../config/api';
import './RecursiveBeliefModal.css';

const getToken = (): string => {
  return localStorage.getItem('authToken') || '';
};

const adminApi = new AdminClientApi(getApiUrl(), getToken);

interface BeliefLayer {
  belief: ClientBelief;
  author?: any;
  layer: number;
  parentBeliefId?: number; // Track which supporting belief created this layer
}

interface RecursiveBeliefModalProps {
  isOpen: boolean;
  onClose: () => void;
  initialBelief: ClientBelief;
  initialAuthor?: any;
}

const RecursiveBeliefModal: React.FC<RecursiveBeliefModalProps> = ({
  isOpen,
  onClose,
  initialBelief,
  initialAuthor,
}) => {
  // Layer management - show up to 3 layers at a time
  const [currentLayers, setCurrentLayers] = useState<BeliefLayer[]>([]);
  const [layerOffset, setLayerOffset] = useState<number>(0); // Which layer range we're viewing

  // Supporting beliefs state
  const [supportingBeliefs, setSupportingBeliefs] = useState<{
    [beliefId: number]: ClientBelief[];
  }>({});
  const [supportingBeliefCounts, setSupportingBeliefCounts] = useState<{
    [beliefId: number]: number;
  }>({});
  const [expandedBeliefs, setExpandedBeliefs] = useState<Set<number>>(new Set());
  const [loadingSupportingBeliefs, setLoadingSupportingBeliefs] = useState<Set<number>>(new Set());
  const [loadingSupportingBeliefCounts, setLoadingSupportingBeliefCounts] =
    useState<boolean>(false);

  // Initialize modal with the initial belief
  useEffect(() => {
    if (isOpen && initialBelief) {
      setCurrentLayers([
        {
          belief: initialBelief,
          author: initialAuthor,
          layer: 0,
        },
      ]);
      setLayerOffset(0);
      setExpandedBeliefs(new Set());
      setSupportingBeliefs({});
      setSupportingBeliefCounts({});

      // Fetch initial supporting belief count
      fetchSupportingBeliefCounts([initialBelief]);
    }
  }, [isOpen, initialBelief, initialAuthor]);

  const fetchSupportingBeliefCounts = async (beliefs: ClientBelief[]): Promise<void> => {
    const beliefIds = beliefs.map((belief) => belief.id);

    if (beliefIds.length === 0) {
      return;
    }

    setLoadingSupportingBeliefCounts(true);

    try {
      const apiParams: GetSupportingBeliefCountFromBeliefsInputInterface = {
        beliefIds,
      };

      const response = await adminApi.getSupportingBeliefCountFromBeliefs(apiParams);

      if (response.success) {
        setSupportingBeliefCounts((prev) => {
          const updated = { ...prev };
          response.supportingBeliefCounts.forEach((count) => {
            updated[count.beliefId] = count.supportingBeliefCount;
          });
          return updated;
        });
      }
    } catch (err: any) {
      console.error('Error fetching supporting belief counts:', err);
    } finally {
      setLoadingSupportingBeliefCounts(false);
    }
  };

  const fetchSupportingBeliefs = async (beliefId: number): Promise<void> => {
    setLoadingSupportingBeliefs((prev) => new Set(prev).add(beliefId));

    try {
      const apiParams: GetBeliefsInputInterface = {
        supportingBeliefsFor: beliefId,
        limit: 50,
      };

      const response = await adminApi.getBeliefs(apiParams);

      if (response.success) {
        setSupportingBeliefs((prev) => ({
          ...prev,
          [beliefId]: response.beliefs,
        }));

        // Fetch counts for the supporting beliefs
        if (response.beliefs.length > 0) {
          fetchSupportingBeliefCounts(response.beliefs);
        }
      }
    } catch (err: any) {
      console.error('Error fetching supporting beliefs:', err);
    } finally {
      setLoadingSupportingBeliefs((prev) => {
        const newSet = new Set(prev);
        newSet.delete(beliefId);
        return newSet;
      });
    }
  };

  const toggleSupportingBeliefs = async (beliefId: number): Promise<void> => {
    const newExpanded = new Set(expandedBeliefs);

    if (newExpanded.has(beliefId)) {
      newExpanded.delete(beliefId);
    } else {
      newExpanded.add(beliefId);
      // Fetch supporting beliefs if not already loaded
      if (!supportingBeliefs[beliefId]) {
        await fetchSupportingBeliefs(beliefId);
      }
    }

    setExpandedBeliefs(newExpanded);
  };

  const expandBelief = (
    belief: ClientBelief,
    currentLayer: number,
    parentBeliefId?: number
  ): void => {
    const newLayer: BeliefLayer = {
      belief,
      layer: currentLayer + 1,
      parentBeliefId: parentBeliefId,
    };

    // Remove all layers after the current layer before adding the new one
    // This prevents multiple layers with the same number when switching supporting beliefs
    const layersUpToCurrentLayer = currentLayers.filter((layer) => layer.layer <= currentLayer);
    const updatedLayers = [...layersUpToCurrentLayer, newLayer];
    setCurrentLayers(updatedLayers);

    // If we now have more than 3 layers visible, adjust the offset
    if (updatedLayers.length - layerOffset > 3) {
      setLayerOffset(layerOffset + 1);
    }

    // Fetch supporting belief count for the new belief
    fetchSupportingBeliefCounts([belief]);
  };

  const navigateToLayer = (targetLayer: number): void => {
    // Remove layers beyond the target layer
    const newLayers = currentLayers.filter((layer) => layer.layer <= targetLayer);
    setCurrentLayers(newLayers);

    // Adjust offset if needed
    const newOffset = Math.max(0, Math.min(layerOffset, newLayers.length - 3));
    setLayerOffset(newOffset);
  };

  const goBackOneLayer = (): void => {
    if (layerOffset > 0) {
      setLayerOffset(layerOffset - 1);
    }
  };

  const canGoForward = (): boolean => {
    return layerOffset + 3 < currentLayers.length;
  };

  const goForwardOneLayer = (): void => {
    if (canGoForward()) {
      setLayerOffset(layerOffset + 1);
    }
  };

  const getBeliefPositionStyle = (position: string) => {
    switch (position?.toLowerCase()) {
      case 'believe':
        return 'believe';
      case 'disbelieve':
        return 'disbelieve';
      case 'unsure':
        return 'unsure';
      default:
        return 'unsure';
    }
  };

  const getUserInitials = (name: string): string => {
    return name
      .split(' ')
      .map((word) => word.charAt(0))
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  // Get the layers to display (up to 3 at a time) - moved before renderBelief
  const visibleLayers = currentLayers.slice(layerOffset, layerOffset + 3);

  const renderBelief = (
    beliefLayer: BeliefLayer,
    isMainBelief: boolean = false,
    layerIndex: number = 0
  ) => {
    const { belief, author, layer, parentBeliefId } = beliefLayer;
    const isExpanded = expandedBeliefs.has(belief.id);
    const supportingBeliefsForBelief = supportingBeliefs[belief.id] || [];
    const isLoadingSupporting = loadingSupportingBeliefs.has(belief.id);
    const supportingCount = supportingBeliefCounts[belief.id] || 0;

    // Check if there's a next layer and get its parent belief ID
    const nextLayerIndex = layerIndex + 1;
    const nextLayer = visibleLayers[nextLayerIndex];
    const nextLayerParentBeliefId = nextLayer?.parentBeliefId;

    return (
      <div
        key={`${layer}-${belief.id}`}
        className={`belief-container ${isMainBelief ? 'main-belief' : 'supporting-belief'}`}
      >
        {/* Belief Header - removed user info */}
        <div className="belief-header">
          <div className="belief-info">
            <div className="belief-position-and-id">
              <span className={`position-badge ${getBeliefPositionStyle(belief.position)}`}>
                {belief.position}
              </span>
              <span className="belief-id">Belief #{belief.id}</span>
            </div>
            <div className="belief-meta">
              {belief.createdAt && (
                <span className="date">{new Date(belief.createdAt).toLocaleDateString()}</span>
              )}
            </div>
          </div>
        </div>

        {/* Belief Content */}
        <div className="belief-content">
          <div className="thought-text">{belief.thought?.text || `Belief ${belief.id}`}</div>
        </div>

        {/* Supporting Beliefs Section */}
        {supportingCount > 0 && (
          <div className="supporting-beliefs-section">
            <button
              className="supporting-beliefs-toggle"
              onClick={() => toggleSupportingBeliefs(belief.id)}
              disabled={isLoadingSupporting}
            >
              {isExpanded ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
              <span>
                {isLoadingSupporting
                  ? 'Loading supporting beliefs...'
                  : `Supporting beliefs (${supportingCount})`}
              </span>
            </button>

            {isExpanded && supportingBeliefsForBelief.length > 0 && (
              <div className="supporting-beliefs-list">
                {supportingBeliefsForBelief.slice(0, 10).map((supportingBelief) => {
                  // Check if this supporting belief created the next layer
                  const isActiveConnection = nextLayerParentBeliefId === supportingBelief.id;
                  const hasNextLayer = nextLayer && isActiveConnection;

                  return (
                    <div
                      key={supportingBelief.id}
                      className={`modal-supporting-belief-item ${isActiveConnection ? 'active-connection' : ''}`}
                    >
                      <div className="modal-supporting-belief-header">
                        <div
                          className={`modal-supporting-belief-position position-badge ${getBeliefPositionStyle(supportingBelief.position)}`}
                        >
                          {supportingBelief.position}
                        </div>
                        {supportingBeliefCounts[supportingBelief.id] !== undefined && (
                          <span className="modal-nested-supporting-count">
                            ({supportingBeliefCounts[supportingBelief.id]} supporting)
                          </span>
                        )}
                      </div>
                      <div className="modal-supporting-belief-content">
                        <button
                          className="modal-supporting-belief-text expandable"
                          onClick={() => expandBelief(supportingBelief, layer, supportingBelief.id)}
                        >
                          {supportingBelief.thought?.text || `Belief ${supportingBelief.id}`}
                        </button>
                      </div>
                      {/* Connection line to next layer */}
                      {hasNextLayer && <div className="connection-line"></div>}
                    </div>
                  );
                })}
                {supportingBeliefsForBelief.length > 10 && (
                  <div className="supporting-beliefs-more">
                    +{supportingBeliefsForBelief.length - 10} more supporting beliefs
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </div>
    );
  };

  if (!isOpen) return null;

  const hasHiddenLayers = layerOffset > 0 || canGoForward();

  return (
    <div className="modal-overlay recursive-belief-modal">
      <div className="modal-content">
        <div className="modal-header">
          <div className="header-content">
            <h3>Recursive Belief Explorer</h3>
            {hasHiddenLayers && (
              <div className="layer-navigation">
                <button className="nav-btn" onClick={goBackOneLayer} disabled={layerOffset === 0}>
                  <ChevronLeft size={16} />
                  Back Layer
                </button>
                <div className="layer-indicator">
                  <Layers size={16} />
                  <span>
                    Viewing layers {layerOffset + 1}-
                    {Math.min(layerOffset + 3, currentLayers.length)} of {currentLayers.length}
                  </span>
                </div>
                <button className="nav-btn" onClick={goForwardOneLayer} disabled={!canGoForward()}>
                  Forward Layer
                  <ChevronRight size={16} />
                </button>
              </div>
            )}
          </div>
          <button className="close-button" onClick={onClose}>
            <X size={20} />
          </button>
        </div>

        <div className="recursive-modal-body">
          {/* User Information - moved outside of layers */}
          {initialAuthor && (
            <div className="explorer-user-info">
              <div className="user-avatar">
                {initialAuthor?.name ? getUserInitials(initialAuthor.name) : <User size={20} />}
              </div>
              <div className="user-details">
                <div className="user-name">{initialAuthor?.name || 'Anonymous User'}</div>
                <div className="user-subtitle">Belief Explorer</div>
              </div>
            </div>
          )}

          {/* Breadcrumb Navigation */}
          {currentLayers.length > 1 && (
            <div className="breadcrumb-nav">
              {currentLayers.map((layer, index) => (
                <React.Fragment key={`breadcrumb-${layer.layer}`}>
                  <button
                    className={`breadcrumb-item ${index >= layerOffset && index < layerOffset + 3 ? 'visible' : 'hidden'}`}
                    onClick={() => navigateToLayer(layer.layer)}
                  >
                    Layer {layer.layer + 1}
                  </button>
                  {index < currentLayers.length - 1 && <ChevronRight size={14} />}
                </React.Fragment>
              ))}
            </div>
          )}

          {/* Belief Layers */}
          <div className="belief-layers">
            {visibleLayers.map((layer, index) => (
              <div key={`layer-${layer.layer}`} className="belief-layer">
                <div className="layer-header">
                  <h4>Layer {layer.layer + 1}</h4>
                </div>
                {renderBelief(layer, index === 0, index)}
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default RecursiveBeliefModal;
